// captionOverlay.js — pure model + render resolution for the Image block's Text
// overlay (a caption placed on one of 9 spots of a 3×3 grid, an overlay scope with
// a Darken↔Lighten amount, and an Auto/Light/Dark text tone with an AA readable-
// contrast guard over arbitrary images). Bucket D / wave D2 / DL-037 (deck
// concepts/builder-blocks/11-image-caption-overlay-editor "N40" +
// 12-viewwheel-image-captions).
//
// SHARED (deck N40/N41): "One Text overlay component — the identical control
// styles Container title bars." This module is the pure, framework-free core the
// editor control (CaptionOverlayControl) and the renderer (CaptionOverlayView)
// both consume, so the exact overlay a user sees in the builder is byte-for-byte
// what the viewer renders — "everything is a shared component".
//
// READABLE-CONTRAST GUARD: an arbitrary photo cannot be pixel-sampled reliably
// (cross-origin S3 / giphy taints a canvas), so legibility is guaranteed
// STRUCTURALLY, not by guessing the image:
//   • scope "behind" → a solid scrim box sits directly behind the caption text
//     (opaque enough for AA regardless of the photo under it).
//   • scope "whole"  → the WHOLE image is darkened (or lightened) by the amount,
//     and the text tone is chosen to contrast that direction.
//   • scope "none"   → no scrim/darken, so the text carries a strong dual
//     text-shadow that keeps it legible on any backdrop.
// Auto tone lands on LIGHT text unless the overlay LIGHTENS the backdrop.
//
// PURE: no React, no DOM, no I/O — the risky mapping is unit-tested in isolation.

/** The 9 caption spots (a 3×3 grid) — the SAME keys the WL-011 PosGrid uses. */
export const CAPTION_POSITIONS = [
  "top-left", "top-center", "top-right",
  "middle-left", "middle-center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
];

/** Overlay scope (deck 11): None · Whole image · Behind text. */
export const CAPTION_SCOPES = ["none", "whole", "behind"];
export const CAPTION_SCOPE_LABEL = {
  none: "None",
  whole: "Whole image",
  behind: "Behind text",
};

/** Text tone (deck 11): Auto · Light · Dark. */
export const CAPTION_TONES = ["auto", "light", "dark"];
export const CAPTION_TONE_LABEL = { auto: "Auto", light: "Light", dark: "Dark" };

export const TEXT_LIGHT = "#FFFFFF";
export const TEXT_DARK = "#123051";

/** Deck default: caption bottom-left, a behind-text scrim, darken 40, auto tone. */
export const defaultCaptionOverlay = () => ({
  text: "",
  position: "bottom-left",
  scope: "behind",
  amount: -40, // negative = darken, positive = lighten
  tone: "auto",
});

const clampAmount = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return -40;
  return Math.min(100, Math.max(-100, Math.round(x)));
};

/**
 * Coerce any stored value into a valid caption overlay, or null (undefined /
 * non-object → null, which the renderer treats as "no overlay", keeping the OFF
 * path byte-identical).
 */
export function normalizeCaptionOverlay(raw) {
  if (!raw || typeof raw !== "object") return null;
  const d = defaultCaptionOverlay();
  return {
    text: typeof raw.text === "string" ? raw.text : "",
    position: CAPTION_POSITIONS.includes(raw.position) ? raw.position : d.position,
    scope: CAPTION_SCOPES.includes(raw.scope) ? raw.scope : d.scope,
    amount: clampAmount(raw.amount == null ? d.amount : raw.amount),
    tone: CAPTION_TONES.includes(raw.tone) ? raw.tone : d.tone,
  };
}

/** True when there is actual caption text to paint. */
export function hasCaption(raw) {
  const o = normalizeCaptionOverlay(raw);
  return !!(o && o.text && o.text.trim());
}

/** True when the amount lightens the backdrop (positive) with a real scope. */
function lightens(o) {
  return o.amount > 0 && o.scope !== "none";
}

/**
 * Whether the caption text should be LIGHT. tone wins when explicit; Auto lands on
 * light text unless the overlay lightens the backdrop (then dark for contrast).
 */
export function isLightText(overlay) {
  const o = normalizeCaptionOverlay(overlay) || defaultCaptionOverlay();
  if (o.tone === "light") return true;
  if (o.tone === "dark") return false;
  return !lightens(o); // auto
}

const VERT = { top: "flex-start", middle: "center", bottom: "flex-end" };
const HORZ = { left: "flex-start", center: "center", right: "flex-end" };
const ALIGN = { left: "left", center: "center", right: "right" };

/**
 * Resolve the pure style descriptor the renderer applies.
 *
 * @param {object} overlay stored captionOverlay prop
 * @returns {{
 *   hasText:boolean, text:string, light:boolean,
 *   imageOverlayStyle:(object|null),  // scope "whole": full-cover darken/lighten layer
 *   wrapperStyle:object,              // absolute inset:0 flex box that positions the caption
 *   textStyle:object                  // the caption text element (scrim/shadow/color/align)
 * }}
 */
export function resolveCaption(overlay) {
  const o = normalizeCaptionOverlay(overlay) || defaultCaptionOverlay();
  const [vraw, hraw] = o.position.split("-");
  const light = isLightText(o);
  const color = light ? TEXT_LIGHT : TEXT_DARK;
  const mag = Math.abs(o.amount) / 100; // 0..1

  // scope "whole": a full-cover layer darkens (black) or lightens (white).
  const imageOverlayStyle =
    o.scope === "whole"
      ? {
          position: "absolute",
          inset: 0,
          background: `rgba(${o.amount < 0 ? "0,0,0" : "255,255,255"}, ${mag.toFixed(3)})`,
          pointerEvents: "none",
        }
      : null;

  // scope "behind": a solid scrim box behind the text guarantees AA on any photo.
  // The scrim is dark for light text, light for dark text; opacity floors at 0.55.
  const scrimOpacity = Math.max(0.55, mag);
  const behindScrim =
    o.scope === "behind"
      ? light
        ? `rgba(0,0,0,${scrimOpacity.toFixed(3)})`
        : `rgba(255,255,255,${scrimOpacity.toFixed(3)})`
      : "transparent";

  // scope "none" (and a light belt-and-braces elsewhere): a strong dual shadow.
  const textShadow =
    o.scope === "none"
      ? light
        ? "0 1px 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.7)"
        : "0 1px 3px rgba(255,255,255,0.9), 0 0 6px rgba(255,255,255,0.7)"
      : "none";

  const wrapperStyle = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: VERT[vraw] || "flex-end",
    justifyContent: HORZ[hraw] || "flex-start",
    padding: 12,
    pointerEvents: "none",
    boxSizing: "border-box",
  };

  const textStyle = {
    color,
    background: behindScrim,
    padding: o.scope === "behind" ? "6px 10px" : "2px 0",
    borderRadius: o.scope === "behind" ? 8 : 0,
    textAlign: ALIGN[hraw] || "left",
    textShadow,
    maxWidth: "100%",
    margin: 0,
    fontWeight: 600,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  };

  return {
    hasText: !!(o.text && o.text.trim()),
    text: o.text,
    light,
    imageOverlayStyle,
    wrapperStyle,
    textStyle,
  };
}

const captionOverlay = {
  CAPTION_POSITIONS,
  CAPTION_SCOPES,
  CAPTION_SCOPE_LABEL,
  CAPTION_TONES,
  CAPTION_TONE_LABEL,
  TEXT_LIGHT,
  TEXT_DARK,
  defaultCaptionOverlay,
  normalizeCaptionOverlay,
  hasCaption,
  isLightText,
  resolveCaption,
};

export default captionOverlay;
